import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { categoryLabel, optimizedImageUrl, optimizedVideoUrl } from '@/lib/gallery';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  useDocumentHead({ path: '/gallery' });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`)
      .then((res) => setItems(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const touchStart = useRef(null);
  const openIndex = open ? items.findIndex((i) => i.id === open.id) : -1;
  const hasPrev = openIndex > 0;
  const hasNext = openIndex >= 0 && openIndex < items.length - 1;

  const goPrev = useCallback(() => { if (hasPrev) setOpen(items[openIndex - 1]); }, [hasPrev, items, openIndex]);
  const goNext = useCallback(() => { if (hasNext) setOpen(items[openIndex + 1]); }, [hasNext, items, openIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(null);
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, goPrev, goNext]);

  // Warm the cache for the neighbours so prev/next feels instant.
  useEffect(() => {
    if (openIndex < 0) return;
    [items[openIndex - 1], items[openIndex + 1]].forEach((n) => {
      if (!n) return;
      const img = new Image();
      img.src = n.media_type === 'video' ? n.poster_url : optimizedImageUrl(n.media_url, 1600);
    });
  }, [items, openIndex]);

  const onTouchStart = (e) => {
    // Don't hijack horizontal drags on the video scrubber.
    if (e.target.tagName === 'VIDEO') { touchStart.current = null; return; }
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) goNext(); else goPrev();
  };

  return (
    <div className="min-h-screen bg-[#f5f0e8] pb-20 md:pb-0 pt-32">
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-8 pb-6">
        <h1 className="font-serif text-3xl sm:text-4xl text-[#1a3a1a]">Photos &amp; Videos</h1>
        <p className="text-gray-600 mt-2">Sourcing, grading, sampling, packing and dispatch.</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-12">
        {loading ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>
        ) : items.length === 0 ? (
          <p className="text-gray-500 py-16 text-center">Photos and videos are coming soon.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setOpen(item)}
                className="relative aspect-square overflow-hidden rounded-xl bg-[#e9e4d8] text-left group"
              >
                <img
                  src={item.media_type === 'video' ? item.poster_url : optimizedImageUrl(item.media_url, 600)}
                  alt={item.alt_text || ''}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {item.media_type === 'video' && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="bg-black/55 text-white rounded-full p-3"><Play size={22} fill="currentColor" /></span>
                  </span>
                )}
                <span className="absolute top-2 left-2 bg-white/90 text-[#1a3a1a] text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full">
                  {categoryLabel(item.category)}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex flex-col items-center justify-center p-4"
          onClick={() => setOpen(null)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          role="dialog"
          aria-modal="true"
        >
          <button type="button" aria-label="Close" className="absolute top-4 right-4 text-white p-2" onClick={() => setOpen(null)}>
            <X size={28} />
          </button>
          {hasPrev && (
            <button
              type="button"
              aria-label="Previous"
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
            >
              <ChevronLeft size={26} />
            </button>
          )}
          {hasNext && (
            <button
              type="button"
              aria-label="Next"
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
            >
              <ChevronRight size={26} />
            </button>
          )}
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {open.media_type === 'video' ? (
              // Mounted only after a tap, so nothing downloads until the visitor asks for it.
              // Starts muted (no autoplay with sound); the controls let them unmute.
              <video
                key={open.id}
                src={optimizedVideoUrl(open.media_url)}
                poster={open.poster_url}
                controls
                autoPlay
                muted
                playsInline
                preload="metadata"
                className="w-full max-h-[75vh] rounded-lg bg-black"
              />
            ) : (
              <img
                src={optimizedImageUrl(open.media_url, 1600)}
                alt={open.alt_text || ''}
                className="w-full max-h-[75vh] object-contain rounded-lg"
              />
            )}
            <div className="mt-3 flex flex-col items-center gap-2 text-center">
              <div className="flex items-center gap-3">
                <span className="bg-white/90 text-[#1a3a1a] text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full">
                  {categoryLabel(open.category)}
                </span>
                <span className="text-white/70 text-xs">{openIndex + 1} / {items.length}</span>
              </div>
              {open.caption && <p className="text-white text-sm md:text-base">{open.caption}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
